

























































































































































































import { addDays, format, startOfDay } from 'date-fns';
import 'url-polyfill';
import Vue from 'vue';
import Component from 'vue-class-component';
import Flatpickr from 'vue-flatpickr-component';
import { mapGetters, mapState } from 'vuex';
import FormInput from '@/components/ui/FormInput.vue';
import FormInputWithIcon from '@/components/ui/FormInputWithIcon.vue';
import Customer from '@/models/Customer';
import NPNumberRange from '@/components/NPNumberRange.vue';
import ParkingZone from '@/models/ParkingZone';
import RecurringGroup from '@/models/RecurringGroup';
import Recurring from '@/models/Recurring';
import { FormatAddress } from '@/utilities/formatters';
import { has } from '@/utilities/helpers';
import moment from 'moment';

const Setup = Vue.extend({
	name: 'RecurringSignUp',
	inject: ['$validator'],
	components: {
		FormInput,
		FormInputWithIcon,
		Flatpickr,
		NPNumberRange,
	},
	props: {
		required: {
			type: Boolean,
			default: false,
		},
		value: {
			required: true,
		},
	},
	data() {
		return {
			nextBillDate: moment(),
		}
	},
	computed: {
		...mapState(['loggedInCustomer']),
		...mapGetters(['customer']),
		...mapGetters('location', ['location', 'parkingZones', 'recurringOptions', 'subLocations']),

		settings(): Locations.Settings {
			return this.$store.getters['location/settings'];
		},

		get_next_charge() {
			if (!(this as any).model) return null;
			const today = moment((this as any).model.target_date);
			const freq = (this as any).model.frequency;
			let calcBillDate;
			let daysInBill = 0;
			switch (freq) {
				case 1: {
					calcBillDate = today.add(1, 'M').startOf('month');
					daysInBill = 30;
					break;
				}
				case 3: {
					let diffMonth = [0, 3, 6, 9].find((quarterStartMonth) => today.month() < quarterStartMonth);
					if (diffMonth === undefined) {
						diffMonth = 0;
						today.add(1, 'y');
					}
					calcBillDate = today.set('month', diffMonth).startOf('month');
					daysInBill = 90;
					break;
				}
				case 6: {
					if (today.month() > 6) {
						const diffMonths = 0;
						calcBillDate = today.set('month', diffMonths).add(1, 'y').startOf('month');
					} else {
						const diffMonths = 6;
						calcBillDate = today.set('month', diffMonths).startOf('month');
					}
					daysInBill = 180;
					break;
				}
				case 12: {
					calcBillDate = today.add(1, 'y').startOf('year');
					daysInBill = 365;
					break;
				}
				default: {
					calcBillDate = today.add(1, 'M').startOf('month');
					daysInBill = moment().daysInMonth();
					break;
				}
			}
			(this as any).nextBillDate = calcBillDate;
			const diffDays = calcBillDate.diff(moment((this as any).model.target_date), 'days') + 1;
			return ((this as any).model.total / daysInBill) * diffDays;
		},
	},
	watch: {
		recurringOptions: {
			handler() {
				const url = new URL(window.location.href);
				const recurring = url.searchParams.get('recurring');
				const type = url.searchParams.get('recurringType');

				(this as any).model = null;

				if (type && recurring) {
					const value = (this as any).recurringOptions.find((option: any) => parseInt(option[type], 10) === parseInt(recurring, 10));

					this.$nextTick(() => {
						(this as any).model = value;
					});
				}

				if ((this as any).recurringOptions.length === 1) {
					this.$nextTick(() => {
						[(this as any).model] = (this as any).recurringOptions;
					});
				}
			},
			immediate: true,
		},
		model: {
			handler(value: ParkingZone | RecurringGroup) {
				if (value && (this as any).model) {
					(this as any).model.target_date = format(new Date(), 'YYYY-MM-DD');
				}
			},
			immediate: true,
		},
	},
});

@Component
export default class RecurringSignUp extends Setup {
	// Props

	// Data
	public group = null;
	public maxTargetDate = addDays(new Date(), 90);
	public today = startOfDay(new Date());

	// State
	public customer!: Customer;

	// Getters
	public location!: Locations.Location;
	public parkingZones!: ParkingZone[];
	public recurring!: Recurring[];
	public recurringOptions!: Array<ParkingZone | RecurringGroup>;
	public subLocations!: Locations.Location[];

	// Vuex
	public loggedInCustomer!: Customer;
	public settings!: Locations.Settings;


	public get availableLocations() {
		if (this.loggedInCustomer && this.loggedInCustomer.recurring) {
			if (this.settings.show_all_recurring_locations) {
				return this.subLocations;
			}
			const loggedInLocations = this.loggedInCustomer.recurring.map((recurring: Recurring) => `${recurring.location_code}-${recurring.location}`);
			const locations = this.subLocations.filter((location) => loggedInLocations.includes(`${location.code}-${location.subLocation}`));
			return locations;
		}

		return null;
	}

	public get model(): ParkingZone | RecurringGroup | null {
		return this.value as ParkingZone | RecurringGroup | null;
	}

	public set model(value: ParkingZone | RecurringGroup | null) {
		this.$emit('input', value);
	}

	public get recurringLocations(): number[] {
		return this.customer.recurring.map((item) => parseInt(item.location as any, 10));
	}

	public getAddress(option: ParkingZone | RecurringGroup): string {
		let zone;
		if (has('parkingZone.zone_id', option)) {
			zone = (option as RecurringGroup).parkingZone as ParkingZone;
		} else {
			zone = option as ParkingZone;
		}

		return FormatAddress({
			address1: zone.address1 || '',
			address2: zone.address2 || '',
			city: zone.city || '',
			state: zone.state || '',
			zip: zone.zip || '',
			phone: zone.phone || '',
			email: zone.email || '',
		}, ' ');
	}

	public getFrequency(frequency: number, adjective: boolean = false): string {
		switch (frequency) {
			case 1:
				return 'monthly';
			case 3:
				return 'quarterly';
			case 6:
				return adjective ? 'semi-annual' : 'semi-annually';
			case 12:
				return adjective ? 'annual' : 'annually';
			default:
				return `${frequency} months`;
		}
	}

	public hasAddress(option: ParkingZone | RecurringGroup): boolean {
		let zone;
		if (has('parkingZone.zone_id', option)) {
			zone = (option as RecurringGroup).parkingZone as ParkingZone;
		} else {
			zone = option as ParkingZone;
		}

		if (zone && (zone.address1 || zone.address2 || zone.city || zone.state)) {
			return true;
		}

		return false;
	}
}
